<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { PowerCredits } from '@/helpers/constants'
import { useRoute, useRouter } from 'vue-router'
import {
  useGetUserCreditTransactionsList,
  useGetPaymentCreditsPricingList,
} from '@gto/api/vue-query'
import GemsBanner from '@/components/Gems/GemsBanner.vue'
import GemsHistory from '@/components/Gems/GemsHistory.vue'
import GemsCards from '@/components/Gems/GemsCards.vue'
import { useStore } from 'vuex'
import GemsSnackbar from '@/components/Gems/GemsSnackbar.vue'
import { UserCreditPaymentStatusQuery, type CreditsPaymentStatus } from '@/helpers/constants'

const store = useStore()
const route = useRoute()
const router = useRouter()

const snackbar = ref(false)
const snackbarCredits = ref<number | null>(null)

function showSnackbarWithCredits(credits: number) {
  snackbarCredits.value = credits
  snackbar.value = true
}

const {
  data,
  isLoading,
  refetch: refetchTransactions,
} = useGetUserCreditTransactionsList({ limit: 999 })
const { data: pricingData } = useGetPaymentCreditsPricingList({})

const credits = computed(() =>
  (pricingData.value?.items ?? []).map(item => ({
    credits: item.credits,
    price: String(item.price),
    base_price: item.base_price ? String(item.base_price) : undefined,
    discount_percentage: item.discount_percentage ? String(item.discount_percentage) : undefined,
    recommended: item.recommended,
  })),
)
const isLoadingValue = computed(() => isLoading.value)
const newCreditsPage = true

type TransactionItem = {
  amount: string
  date_created: string
  name: string
  description: string
}

async function refetchUserCreditInfo() {
  await refetchTransactions()
  await store.dispatch('api_call', {
    method: 'get',
    hasCancel: false,
    action: 'updateUserInfo',
    endpoint: 'account/settings/',
    mutation: 'api_account_settings',
  })
}

function getLastSuccessfulCredit(transactionsValue: TransactionItem[]) {
  if (!transactionsValue.length) return null
  const sorted = [...transactionsValue].sort(
    (a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime(),
  )
  const last = sorted.find(tx => Number(tx.amount) > 0)
  return last ? Math.floor(Number(last.amount)) : null
}

const transactionsValue = computed<TransactionItem[]>(() =>
  (data.value?.items ?? []).map(item => ({
    amount: String(item.amount),
    date_created: item.date_created,
    name: item.name,
    description: item.description,
  })),
)

watch(
  route,
  async () => {
    if (
      UserCreditPaymentStatusQuery in route.query &&
      route.query[UserCreditPaymentStatusQuery] === ('successful' as CreditsPaymentStatus)
    ) {
      await refetchUserCreditInfo()

      setTimeout(() => {
        const lastSuccessfulCredit = getLastSuccessfulCredit(transactionsValue.value ?? [])
        if (lastSuccessfulCredit != null) {
          showSnackbarWithCredits(lastSuccessfulCredit)
        }
      }, 200)

      const { [UserCreditPaymentStatusQuery]: _removed, ...rest } = route.query
      router.replace({ query: rest })
    }
  },
  { immediate: true, deep: true },
)
</script>

<template>
  <div v-if="newCreditsPage">
    <transition name="fade">
      <GemsSnackbar
        v-if="snackbarCredits !== null"
        :credits="snackbarCredits"
        @close="snackbarCredits = null"
      />
    </transition>
    <h1 style="color: var(--palette-primary-main); font: var(--font-subtitle1)">
      {{ PowerCredits }}
    </h1>
    <GemsBanner style="margin-bottom: var(--app-gap-6)" />
    <GemsCards :credits="credits" />
    <GemsHistory
      :items="transactionsValue"
      :isLoading="isLoadingValue"
    />
  </div>
</template>

<style lang="scss" scoped>
.bottom_container {
  min-height: 30rem;
  gap: 6rem;
  --clr-box: #405642;
}

@media (max-width: 1023px) {
  .bottom_container {
    flex-direction: column;
  }
}

.balance_box {
  --clr-type: var(--clr-box);
  --clr-back: linear-gradient(225deg, var(--clr-type), transparent);
  display: flex;
  flex-direction: column;

  padding: 1rem;
  border-radius: 1.875rem;
  background: var(--clr-back);
  border: 0.15rem solid var(--clr-box);

  min-height: 14.2rem;
  max-height: 14.2rem;
}

.transaction_box {
  --clr-transaction-border: #1f1f1f;
  --clr-transaction-background: #191919;

  display: flex;
  flex-direction: column;

  border-radius: 1.875rem;
  border: 0.15rem solid var(--clr-transaction-border);

  &_content {
    gap: 0.8rem;
    max-height: 30rem;
    overflow-y: scroll;
    overflow-x: hidden;
    margin: 0 0 1rem 0;
  }

  &_item {
    --clr-type: var(--clr-box);
    --clr-back: linear-gradient(225deg, var(--clr-type), transparent);
    display: flex;
    flex-direction: column;

    margin: 0 1rem 0 1rem;
    gap: 1rem;
    background: var(--clr-transaction-background);
    padding: 1rem;
    border-radius: 0.5rem;
    border: 0.15rem solid var(--clr-transaction-border);
  }
}
</style>
